/* eslint-disable complexity */
import { SkeletonLoader } from '@toggle/design-system';
import { Entity, PriceHistory } from '@toggle/toggle';
import classnames from 'classnames';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePriceDisplay } from '~/react-query-hooks/prices/usePriceDisplay';

import { PriceSummaryDisplay } from './components/PriceSummaryDisplay';
import styles from './MarketReactionLivePrice.module.scss';

const EASTERN_TIMEZONE = 'America/New_York';

const getEasternTimezoneAbbr = (date: Date): string => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIMEZONE,
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find(part => part.type === 'timeZoneName');
  return tzPart?.value ?? 'ET';
};

export interface MarketReactionLivePriceProps {
  entity: Entity;
  isWindowComplete: boolean;
  eventProportionChange?: number;
  prevCloseData?: PriceHistory[number];
  lastKnownPrice?: number;
  lastKnownDate?: string;
}

export const MarketReactionLivePrice = ({
  entity,
  isWindowComplete,
  eventProportionChange,
  prevCloseData,
  lastKnownPrice,
  lastKnownDate,
}: MarketReactionLivePriceProps) => {
  const { t } = useTranslation(['analyze']);
  const { isLoading, isValid, priceData } = usePriceDisplay(entity);

  if (isLoading) {
    return (
      <div data-testid="insights-live-price-loading">
        <SkeletonLoader
          areas={[{ styles: { width: '120px', height: '40px' } }]}
          withWrapper={false}
        />
      </div>
    );
  }

  if (!isValid || !priceData || eventProportionChange === undefined) {
    return null;
  }

  const { price, mappedEntity, priceFormatter } = priceData;
  const { marketHours } = price.primary;

  const calcPercentChange = (currentPrice?: number) =>
    prevCloseData?.close && currentPrice
      ? ((currentPrice - prevCloseData.close) / prevCloseData.close) * 100
      : undefined;

  const isOutsideHoursLive =
    !isWindowComplete &&
    (marketHours.isPreMarket || marketHours.isAfterMarket) &&
    price.secondary?.price !== undefined &&
    price.secondary?.isLive;

  const isMarketHoursLive =
    !isWindowComplete &&
    !isOutsideHoursLive &&
    marketHours.isMarketHours &&
    price.primary?.isLive &&
    price.primary?.price !== undefined;

  const priceColumn = isOutsideHoursLive
    ? {
        price: price.secondary?.price
          ? priceFormatter(price.secondary?.price)
          : undefined,
        currency: mappedEntity.currency,
        label: (
          <div className={styles.liveLabelContainer}>
            <span
              className={classnames(
                styles.outsideHoursTag,
                styles[price.secondary?.outsideHours?.tag ?? 'pre']
              )}
            >
              {price.secondary?.outsideHours?.label}
            </span>
            <span className={styles.liveLabel}>
              {t('analyze:analysis.marketOpen')}
            </span>
          </div>
        ),
      }
    : isMarketHoursLive
    ? {
        price: price.primary?.price
          ? priceFormatter(price.primary?.price)
          : undefined,
        currency: mappedEntity.currency,
        label: (
          <div className={styles.liveLabelContainer}>
            <span className={styles.liveLabel}>
              {t('analyze:analysis.livePrice')}
            </span>
          </div>
        ),
      }
    : {
        price: lastKnownPrice ? priceFormatter(lastKnownPrice) : undefined,
        currency: mappedEntity.currency,
        label: lastKnownDate && (
          <span className={styles.dateSpan}>
            {t('analyze:analysis.asOfDate', {
              date: `${formatInTimeZone(
                new Date(lastKnownDate),
                EASTERN_TIMEZONE,
                'MMM d, h:mm aaa'
              )} ${getEasternTimezoneAbbr(new Date(lastKnownDate))}`,
            })}
          </span>
        ),
      };

  const percentageChange = isOutsideHoursLive
    ? calcPercentChange(price.secondary?.price)
    : isMarketHoursLive
    ? calcPercentChange(price.primary?.price)
    : eventProportionChange;

  return (
    <div data-testid="market-reaction-live-price">
      <div className={styles.proportionChangeContainer}>
        <PriceSummaryDisplay
          priceColumn={priceColumn}
          percentageChange={percentageChange}
          prevCloseTime={prevCloseData?.time}
        />
      </div>
    </div>
  );
};
